import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';

import type { TranscriptElement } from '@src/types.ts/transcript';

export interface SpeakerSegment {
  startTime: number;
  endTime: number;
  speakerColor: string;
}

export interface WaveformBucket {
  max: number;
  rms: number;
}

/**
 * Builds speaker segments from a flat TranscriptElement[] array.
 * Groups consecutive elements with the same speaker_label into segments
 * with time ranges and speaker colors.
 */
export function buildSpeakerSegmentsFromElements(
  elements: TranscriptElement[],
  colorResolver?: (label: string) => string,
): SpeakerSegment[] {
  const getColor = colorResolver || getSpeakerColorFromLabel;
  const segments: SpeakerSegment[] = [];
  let currentLabel: string | null = null;
  let segmentStart: number | null = null;
  let segmentEnd: number | null = null;

  for (const el of elements) {
    if (el.type !== 'text' || el.ts == null || el.end_ts == null) continue;
    if (el.state === 'removed' || el.state === 'cut') continue;

    const label = el.speaker_label || null;

    if (label !== currentLabel) {
      if (currentLabel && segmentStart != null && segmentEnd != null) {
        segments.push({
          startTime: segmentStart,
          endTime: segmentEnd,
          speakerColor: getColor(currentLabel),
        });
      }
      currentLabel = label;
      segmentStart = el.ts;
      segmentEnd = el.end_ts;
    } else {
      if (el.end_ts > (segmentEnd ?? 0)) {
        segmentEnd = el.end_ts;
      }
    }
  }

  if (currentLabel && segmentStart != null && segmentEnd != null) {
    segments.push({
      startTime: segmentStart,
      endTime: segmentEnd,
      speakerColor: getColor(currentLabel),
    });
  }

  return segments;
}

/** Target number of pre-downsampled buckets — enough for any zoom level */
const DOWNSAMPLE_BUCKETS = 4000;

/**
 * Pre-downsample raw waveform data into fixed-size buckets.
 * Each bucket stores the pre-computed max amplitude and RMS value.
 * This runs once when waveform data loads (~7M samples → 4000 buckets).
 */
export function downsampleWaveform(data: number[]): WaveformBucket[] {
  if (data.length === 0) return [];

  const numBuckets = Math.min(DOWNSAMPLE_BUCKETS, data.length);
  const bucketSize = data.length / numBuckets;
  const buckets: WaveformBucket[] = new Array(numBuckets);

  for (let i = 0; i < numBuckets; i++) {
    const start = Math.floor(i * bucketSize);
    const end = Math.floor((i + 1) * bucketSize);
    let max = 0;
    let sumSq = 0;
    for (let j = start; j < end; j++) {
      const v = data[j];
      if (v > max) max = v;
      sumSq += v * v;
    }
    const count = end - start;
    buckets[i] = { max, rms: count > 0 ? Math.sqrt(sumSq / count) : 0 };
  }

  return buckets;
}

/**
 * Downsample a composed dub `AudioBuffer` (PLAT-10620) into a positive peak
 * array spanning the full story timeline `[0, durationSec]`. The dub buffer
 * (from `composeDubTrack`) only runs to the last segment's end, so buckets past
 * `buffer.length` are left `0` (silence) — this keeps the peaks mapping by
 * `time / duration` exactly like the original waveform data, so gaps and the
 * un-dubbed tail render flat and aligned with the transcript clock/playhead.
 *
 * Values are scaled to the 0–255 domain the original waveform data uses; the
 * waveform draw path self-normalizes, so only relative amplitude matters.
 */
export function computeDubPeaks(
  buffer: AudioBuffer,
  durationSec: number,
  numPoints = 8000,
): number[] {
  const peaks = new Array<number>(numPoints).fill(0);
  if (durationSec <= 0 || buffer.length === 0) return peaks;

  const channel = buffer.getChannelData(0); // renditions are mono
  const { sampleRate } = buffer;
  const secondsPerBucket = durationSec / numPoints;

  for (let i = 0; i < numPoints; i++) {
    const startSample = Math.floor(i * secondsPerBucket * sampleRate);
    if (startSample >= channel.length) break; // rest of the timeline stays silent
    const endSample = Math.min(
      channel.length,
      Math.floor((i + 1) * secondsPerBucket * sampleRate),
    );
    let max = 0;
    for (let j = startSample; j < endSample; j++) {
      const v = Math.abs(channel[j]);
      if (v > max) max = v;
    }
    peaks[i] = max * 255;
  }

  return peaks;
}

/**
 * Remap a precomputed original `WaveformData` blob (PLAT-10737) onto the story
 * timeline `[0, durationSec]` — the same transcript clock `computeDubPeaks` maps
 * by. The blob is sampled uniformly over its own media length (`wf.duration`),
 * which is typically longer than the transcript duration (trailing non-speech
 * after the last word). Without this remap the draw path stretches the whole blob
 * across the width by array index, pushing the final spoken segment left of the
 * right edge and leaving a gap. Buckets past the media end stay `0` (silence),
 * exactly like the dub's un-covered tail — so Original and Dubbed line up
 * bar-for-bar.
 *
 * Values keep the blob's native amplitude scale; the waveform draw path
 * self-normalizes, so only relative amplitude matters.
 */
export function computeOriginalPeaks(
  wf: { toJSON: () => { data: number[] }; duration: number },
  durationSec: number,
  numPoints = 8000,
): number[] {
  const peaks = new Array<number>(numPoints).fill(0);
  const mediaDuration = wf.duration;
  if (durationSec <= 0 || mediaDuration <= 0) return peaks;

  const data = wf.toJSON().data;
  if (data.length === 0) return peaks;

  const secondsPerBucket = durationSec / numPoints;

  for (let i = 0; i < numPoints; i++) {
    const bucketTime = i * secondsPerBucket;
    if (bucketTime >= mediaDuration) break; // past the media → stays silent
    const startIdx = Math.floor((bucketTime / mediaDuration) * data.length);
    const rawEndIdx = Math.floor(
      (((i + 1) * secondsPerBucket) / mediaDuration) * data.length,
    );
    // Read at least one sample so a transcript longer-per-bucket than the blob's
    // resolution never skips data.
    const endIdx = Math.min(data.length, Math.max(rawEndIdx, startIdx + 1));
    let max = 0;
    for (let j = startIdx; j < endIdx; j++) {
      const v = data[j];
      if (v > max) max = v;
    }
    peaks[i] = max;
  }

  return peaks;
}

/**
 * Derive duration from transcript elements, falling back to a provided duration.
 */
export function getDurationFromElements(
  elements: TranscriptElement[] | undefined | null,
  fallbackDuration: number,
): number {
  if (elements && elements.length > 0) {
    for (let i = elements.length - 1; i >= 0; i--) {
      const endTs = elements[i].end_ts;
      if (endTs != null && typeof endTs === 'number') {
        return endTs;
      }
    }
  }
  return fallbackDuration || 0;
}
